/**
 * The confirmation dialog, at 11 call sites (`TicketDetail` ×9,
 * `ApprovalQueue`, `Settings`).
 *
 * Since P36 it is **a thin wrapper over `Dialog`** and owns no behaviour of its
 * own. That is the whole point: the five things it was missing — `role`,
 * `aria-modal`, Escape, a focus trap, focus return — arrive at all 11 sites
 * without 11 edits, and the props below are unchanged, so no call site moved.
 *
 * Two things did change on screen, and both are corrections rather than
 * decisions:
 *
 *   - **The confirm button is no longer a filled `bg-red-600` / `bg-yellow-600`
 *     (S2-09).** Reference §7: destructive is white with an `--err-edge` border
 *     and `--err` text, never a fill. The ticket screen's own "Remove the
 *     assignment" had been doing that correctly all along, so this file is what
 *     was disagreeing with the app, not the other way round.
 *   - The `warning` variant's confirm button is an ordinary primary. A warning
 *     is not a destruction; it was drawn in a yellow fill that read as a third
 *     severity nothing else in the app uses.
 */
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { Dialog, DialogCancel } from './Dialog';

interface Props {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning';
    onConfirm: () => void;
    onCancel: () => void;
}

export function ConfirmModal({
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    onConfirm,
    onCancel,
}: Props) {
    return (
        <Dialog
            title={title}
            onClose={onCancel}
            icon={
                <div className={`rounded-full p-2 ${variant === 'danger' ? 'bg-err-50' : 'bg-warn-50'}`}>
                    <AlertTriangle
                        aria-hidden="true"
                        className={`h-5 w-5 ${variant === 'danger' ? 'text-err' : 'text-warn-ink'}`}
                    />
                </div>
            }
            footer={
                <>
                    <DialogCancel label={cancelLabel} onClick={onCancel} />
                    <Button
                        variant={variant === 'danger' ? 'destructive' : 'primary'}
                        size="sm"
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            {message}
        </Dialog>
    );
}
